import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyChatbots, createChatbotSession, logout, getMonthlyUsage } from '../services/api';
import { Chatbot } from '../types';
import doordaAiLogo from '../assets/doorda_ai_logo.png';
import { ChevronDown } from 'lucide-react';

const ChatbotView: React.FC = () => {
  const navigate = useNavigate();
  const [chatbots, setChatbots] = useState<Chatbot[]>([]);
  const [selectedChatbot, setSelectedChatbot] = useState<{ url: string; name: string } | null>(null);
  const [error, setError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDebug, setIsDebug] = useState(true); // Toggle state for debug
  const [showReasoning, setShowReasoning] = useState(false); // Toggle state for show reasoning
  const [usage, setUsage] = useState<{ chatbot_usage: number | null, total_usage: number | null }>({ chatbot_usage: null, total_usage: null });

  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        const response = await getMyChatbots();
        setChatbots(response.chatbots);
        // If only one chatbot, select it and fetch usage
        if (response.chatbots.length === 1) {
          handleChatbotSelect(response.chatbots[0].id);
        }
      } catch (err) {
        setError('Failed to fetch chatbots');
      }
    };
    fetchChatbots();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleWindowBlur = () => {
      // Give it a tick to make sure focus moves into the iframe
      setTimeout(() => {
        if (document.activeElement?.tagName === 'IFRAME') {
          setIsDropdownOpen(false);
        }
      }, 0);
    };
  
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, []);
  
  // When debug toggle is switched, reload session if a chatbot is selected
  useEffect(() => {
    if (selectedChatbot && selectedChatbot.name) {
      // Find the chatbot id from the chatbots list by name
      const current = chatbots.find((c: Chatbot) => c.name === selectedChatbot.name);
      if (current) {
        handleChatbotSelect(current.id);
        fetchUsage(current.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDebug, showReasoning]);

  // Always fetch total usage at mount
  useEffect(() => {
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch usage for the selected chatbot
  const fetchUsage = async (chatbotId?: string) => {
    try {
      const usageData = await getMonthlyUsage(chatbotId);
      setUsage({
        chatbot_usage: usageData.chatbot_usage,
        total_usage: usageData.total_usage
      });
    } catch (err) {
      setUsage({ chatbot_usage: null, total_usage: null });
    }
  };

  const handleChatbotSelect = async (chatbotId: string) => {
    setIsDropdownOpen(false);
    setIsLoading(true);
    try {
      const session = await createChatbotSession(chatbotId, isDebug, showReasoning); // Pass debug state
      setSelectedChatbot({
        url: session.url,
        name: session.chatbot_name
      });
      await fetchUsage(chatbotId);
    } catch (err) {
      setError('Failed to create chatbot session');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="h-dvh flex flex-col">
      
      <div className="h-16 bg-white border-b flex items-center justify-between px-4 md:px-6 lg:px-10">
        <img src={doordaAiLogo} alt="Doorda Logo" className="h-6 md:h-8" />
        <div className="flex-1 flex justify-center">
          {chatbots.length > 1 ? (
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center justify-between w-full px-2 md:px-4 py-2 md:py-3 bg-white hover:bg-gray-100 transition-colors focus:outline-none rounded-lg min-w-[180px] md:min-w-[300px] border-none"
                disabled={isLoading}
              >
                <span className="text-base md:text-lg truncate max-w-[180px] md:max-w-[280px]">
                  {selectedChatbot?.name || 'Select a chatbot'}
                </span>
                <ChevronDown 
                  size={18}
                  color="#9f9f9f" 
                  className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg z-50 py-2 max-h-[60vh] overflow-auto shadow-lg">
                  {chatbots.map((chatbot) => (
                    <button
                      key={chatbot.id}
                      onClick={() => handleChatbotSelect(chatbot.id)}
                      className="w-full px-3 md:px-4 py-2 md:py-3 text-left hover:bg-gray-100 transition-colors focus:outline-none border-none text-sm md:text-base"
                    >
                      {chatbot.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-base md:text-lg font-medium truncate max-w-[200px] md:max-w-none">
              {selectedChatbot?.name}
            </div>
          )}
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="inline-flex text-base md:text-xl font-medium text-doorda-red hover:bg-gray-100 transition-colors rounded-lg"
        >
          Logout
        </button>
      </div>

      {error && (
        <div className="p-3 md:p-4 bg-red-50">
          <div className="text-xs md:text-sm text-red-700">{error}</div>
        </div>
      )}
      <div className="flex-1 relative">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <div className="text-base md:text-lg text-gray-500">Loading chatbot...</div>
          </div>
        ) : selectedChatbot && (
          <iframe
            allow="clipboard-read; clipboard-write"
            src={selectedChatbot.url}
            className="w-full h-full border-0 absolute inset-0"
            title={selectedChatbot.name}
          />
        )}
      </div>

      <div className="h-16 bg-white border-t flex items-center justify-between px-4 md:px-6 lg:px-10">

        <div className="flex flex-col">
          <span className="text-sm text-gray-600 font-medium md:text-base">
            Number of Hits this Month
          </span>
          <span className="text-xs md:text-sm text-gray-500">
            Current Chatbot: {usage.chatbot_usage ?? '-'} | Total: {usage.total_usage ?? '-'}
          </span>
        </div>

        {/* Toggle Switches */}
        <div className="flex flex-col md:flex-row items-end md:items-center space-y-2 md:space-y-0 md:space-x-6">
          <div className="flex items-center">
            <span className="text-xs md:text-sm lg:text-base select-none">Debug Mode</span>
            <span
                onClick={() => setIsDebug((prev: boolean) => !prev)}
                className="relative inline-block w-8 h-5 md:w-10 md:h-6 align-middle select-none transition duration-200 ease-in ml-2 cursor-pointer"
                title="Toggle debug mode"
                role="checkbox"
                aria-checked={isDebug}
                tabIndex={0}
                onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') setIsDebug(prev => !prev); }}
              >
              <span
                className={`absolute left-0 top-0 w-8 h-5 md:w-10 md:h-6 rounded-full transition-colors duration-200 ${isDebug ? 'bg-green-500' : 'bg-gray-300'}`}
              ></span>
              <span
                className={`absolute left-0 top-0 w-4 h-4 md:w-6 md:h-6 bg-white border border-gray-300 rounded-full shadow transform transition-transform duration-200 ${isDebug ? 'translate-x-4 md:translate-x-4' : ''}`}
                style={{ zIndex: 1 }}
              ></span>
            </span>
          </div>

          <div className="flex items-center">
            <span className="text-xs md:text-sm lg:text-base select-none">Show Reasoning</span>
            <span
                onClick={() => setShowReasoning((prev: boolean) => !prev)}
                className="relative inline-block w-8 h-5 md:w-10 md:h-6 align-middle select-none transition duration-200 ease-in ml-2 cursor-pointer"
                title="Toggle show reasoning"
                role="checkbox"
                aria-checked={showReasoning}
                tabIndex={0}
                onKeyPress={e => { if (e.key === 'Enter' || e.key === ' ') setShowReasoning(prev => !prev); }}
              >
              <span
                className={`absolute left-0 top-0 w-8 h-5 md:w-10 md:h-6 rounded-full transition-colors duration-200 ${showReasoning ? 'bg-green-500' : 'bg-gray-300'}`}
              ></span>
              <span
                className={`absolute left-0 top-0 w-4 h-4 md:w-6 md:h-6 bg-white border border-gray-300 rounded-full shadow transform transition-transform duration-200 ${showReasoning ? 'translate-x-4 md:translate-x-4' : ''}`}
                style={{ zIndex: 1 }}
              ></span>
            </span>
          </div>
        </div>

      </div>

    </div>
  );
};

export default ChatbotView; 